/* Copyright (c) 2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import { dateFormat } from "~/constants/dateFormats";
import { createCountFilterDefinition } from "~/hooks/useAppliedFilters/definitionFactories";
import { AppliedFilterDefinition } from "~/hooks/useAppliedFilters/types";
import { QueryState } from "~/hooks/useQueryState/useQueryState";
import { CustomActionQuery } from "../types";

type DefinitionKey = "date" | "types" | "scopes" | "plans";
type CustomActionQueryState = CustomActionQuery & QueryState;

type Definition = AppliedFilterDefinition<
  CustomActionQueryState,
  DefinitionKey
>;

type CustomActionAppliedFilterLabels = {
  planNamesById?: Record<string, string>;
  typeLabelsByValue?: Record<string, string>;
  scopeLabelsByValue?: Record<string, string>;
};

export const createCustomActionAppliedFilters = ({
  planNamesById = {},
  typeLabelsByValue = {},
  scopeLabelsByValue = {},
}: CustomActionAppliedFilterLabels = {}): Definition[] => [
  {
    key: "date",
    label: "Creation Date",
    enableValuePopover: false,
    getValues: (queryState) =>
      queryState.startDateUTC && queryState.endDateUTC
        ? [queryState.startDateUTC, queryState.endDateUTC]
        : [],
    getValueDisplay: (queryState) =>
      `${dayjs(queryState.startDateUTC).format(dateFormat.shortDate)} - ${dayjs(
        queryState.endDateUTC
      ).format(dateFormat.shortDate)}`,
    clear: () => ({
      startDateUTC: undefined,
      endDateUTC: undefined,
    }),
  },
  createCountFilterDefinition<CustomActionQueryState, "types">({
    key: "types",
    label: "Type",
    labels: { singular: "Type", plural: "Types" },
    getValues: (queryState) => queryState.types,
    getExcluded: (queryState) => queryState.excludeTypes,
    getValueLabel: (value) => typeLabelsByValue[value] ?? value,
    clear: () => ({ types: [], excludeTypes: false }),
    applyValuesPatch: (queryState, values) => ({
      types: values as CustomActionQuery["types"],
      excludeTypes: values.length > 0 ? queryState.excludeTypes : false,
    }),
  }),
  createCountFilterDefinition<CustomActionQueryState, "scopes">({
    key: "scopes",
    label: "Scope",
    labels: { singular: "Scope", plural: "Scopes" },
    getValues: (queryState) => queryState.scopes,
    getExcluded: (queryState) => queryState.excludeScopes,
    getValueLabel: (value) => scopeLabelsByValue[value] ?? value,
    clear: () => ({ scopes: [], excludeScopes: false }),
    applyValuesPatch: (queryState, values) => ({
      scopes: values as CustomActionQuery["scopes"],
      excludeScopes: values.length > 0 ? queryState.excludeScopes : false,
    }),
  }),
  createCountFilterDefinition<CustomActionQueryState, "plans">({
    key: "plans",
    label: "Related Plan",
    labels: { singular: "Related Plan", plural: "Related Plans" },
    getValues: (queryState) => queryState.planIds,
    getExcluded: (queryState) => queryState.excludePlanIds,
    getValueLabel: (value) => planNamesById[value] ?? value,
    clear: () => ({ planIds: [], excludePlanIds: false }),
    applyValuesPatch: (queryState, values) => ({
      planIds: values,
      excludePlanIds: values.length > 0 ? queryState.excludePlanIds : false,
    }),
  }),
];
